/** 3D vector */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Tempo categories */
export type Tempo = 'slow' | 'medium' | 'fast';

/** Transition categories */
export type Transition = 'smooth' | 'medium' | 'quick';

/** Handedness */
export type Handedness = 'right' | 'left';

/** Player physical profile */
export interface PlayerProfile {
  height: number;           // inches
  wristToFloor: number;     // inches
  weight: number;           // lbs
  handedness: Handedness;
  handicap: number;
}

/** Swing parameters from user input or API */
export interface SwingParams {
  // Core ball flight
  clubSpeed: number;        // mph
  ballSpeed: number;        // mph
  launchAngle: number;      // degrees vertical
  launchDirection: number;  // degrees horizontal (+ = right for RH)
  spinRate: number;         // rpm
  spinAxis: number;         // degrees tilt from pure backspin (+ = fade/slice)

  // Impact / swing path
  attackAngle: number;      // degrees (+ = up, - = down)
  clubPath: number;         // degrees (+ = in-to-out)
  faceAngle: number;        // degrees (+ = open)

  // Swing characteristics
  tempo: Tempo;
  transition: Transition;
}

/** State vector for the ball flight ODE */
export interface FlightState {
  position: Vec3;
  velocity: Vec3;
  spinRate: number;        // rpm (initial, before decay)
  spinAxis: Vec3;          // unit vector
  time: number;            // seconds since launch
}

/** A recorded frame for replay/tracer */
export interface FlightFrame {
  time: number;
  position: Vec3;
  velocity: Vec3;
  spinRate: number;
  phase?: 'flight' | 'landing' | 'ground';
}

/** Shot analytics computed after flight completes */
export interface ShotResult {
  carryDistance: number;    // meters
  totalDistance: number;    // meters
  maxHeight: number;       // meters
  flightTime: number;      // seconds
  landingAngle: number;    // degrees
  landingSpeed: number;    // m/s
  offline: number;         // meters (+ = right)
  apexPosition: Vec3;
  landingPosition: Vec3;
  restPosition: Vec3;
}

/** Surface type for ground physics */
export enum SurfaceType {
  Fairway = 'fairway',
  Rough = 'rough',
  Green = 'green',
  Bunker = 'bunker',
}

/** Surface physics properties */
export interface SurfaceProperties {
  restitution: number;
  friction: number;
  /** Dimensionless rolling resistance; deceleration = coefficient * gravity. */
  rollingResistance: number;
  /** Effective vegetation/ploughing drag (1/s), only while settled on turf. */
  settledDrag: number;
}

/** Simulation phase */
export enum SimPhase {
  Idle = 'idle',
  Flight = 'flight',
  Ground = 'ground',
  Rest = 'rest',
  Replay = 'replay',
}

/** Environment configuration for air density and wind */
export interface EnvironmentConfig {
  altitude: number;          // feet above sea level
  temperature: number;       // °F
  windSpeed: number;         // mph
  windDirection: number;     // degrees (0 = headwind, 90 = left-to-right)
}

/** Club preset data */
export interface ClubPreset {
  name: string;
  clubSpeed: number;        // mph
  ballSpeed: number;        // mph
  launchAngle: number;      // degrees
  spinRate: number;         // rpm
  spinAxis: number;         // degrees
  attackAngle: number;      // degrees
  clubPath: number;         // degrees
  faceAngle: number;        // degrees
}
